import { memo, useCallback, useMemo, useRef, useState, ReactElement } from 'react';
import { EditOutlined, DeleteOutlined, EyeOutlined, CheckOutlined } from '@ant-design/icons';
import { Button, Flex, Modal, Space, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { Table, TableComponent, TableProps, notification } from '@ohpen/enso-ui';
import { Guard } from '@ohpen/react-authz';
import { lockActionTitle, getApiErrorMessage, changeRequestNotificationMessage } from '@pmp/utils';
import { ActionEntityType } from '@pmp/api';
import { useListTableState, useFeatureToggle } from '@pmp/hooks';

import { ProcurationFeeAssignmentsListProps, GenericChangeResponse } from '../interfaces';
import { useChangeLogAction } from '../../changeLog';

const { Text } = Typography;
const rowKey = 'id';

const labelOf = (value: unknown): string | undefined =>
  typeof value === 'object' && value !== null ? ((value as { value?: string }).value ?? undefined) : (value as string | undefined);

/** An assignment is named by the fee it assigns and the rule it assigns it under. */
const toChangeLogRecord = (record: unknown) => {
  const assignment = record as { id?: string; procurationFee?: unknown; procurationFeeRule?: unknown };
  const name = [labelOf(assignment.procurationFee), labelOf(assignment.procurationFeeRule)].filter(Boolean).join(' / ');

  return { id: assignment.id ?? '', name: name || (assignment.id ?? '') };
};

const CHANGE_LOG_ENTITY_TYPES: Partial<
  Record<ActionEntityType, ActionEntityType.DEFAULT_PROCURATION_FEE_ASSIGNMENTS | ActionEntityType.FIRM_PROCURATION_FEE_ASSIGNMENTS>
> = {
  [ActionEntityType.LENDERS]: ActionEntityType.DEFAULT_PROCURATION_FEE_ASSIGNMENTS,
  [ActionEntityType.FIRMS]: ActionEntityType.FIRM_PROCURATION_FEE_ASSIGNMENTS,
};

export const ProcurationFeeAssignmentsListBase = <T extends { lockedByActionId?: string }, U extends GenericChangeResponse<any>>({
  procurationFees,
  loading,
  valueLists,
  readonly,
  parentEntityType,
  columns,
  onCreate,
  onView,
  onEdit,
  onDelete,
  refreshTable,
  enableRowDnD,
  onRowDnDEnd,
  guardFlags = [],
  permissionKey,
  lockedKey = 'lockedByActionId',
}: ProcurationFeeAssignmentsListProps<T, U>): ReactElement => {
  const tableRef = useRef<TableComponent | null>(null);
  const [editOrder, setEditOrder] = useState(false);
  const [order, setOrder] = useState<T[] | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const { t } = useTranslation();
  const { changeLogAction, changeLogDrawer } = useChangeLogAction<T>(
    parentEntityType ? CHANGE_LOG_ENTITY_TYPES[parentEntityType] : undefined,
    toChangeLogRecord,
  );
  const enableFourEyes = useFeatureToggle(['enableFourEyes']);
  // The firm's own assignments report nothing: the page context carries no parent entity, so a selected
  // row could not be resolved back to the firm it belongs to.
  const tableState = useListTableState<T>(
    parentEntityType === ActionEntityType.LENDERS ? ActionEntityType.DEFAULT_PROCURATION_FEE_ASSIGNMENTS : undefined,
    rowKey,
    tableRef,
    columns,
  );

  const isOrderLocked = useMemo(() => procurationFees?.some(item => !!item[lockedKey]), [procurationFees, lockedKey]);
  const orderLockedByActionId = useMemo(
    () => (isOrderLocked ? (procurationFees?.find(x => x[lockedKey])?.[lockedKey] as string) : ''),
    [procurationFees, isOrderLocked, lockedKey],
  );

  const onApplyOrder = useCallback(() => setShowConfirmModal(true), []);
  const handleConfirmApply = useCallback(() => {
    if (!order || !onRowDnDEnd) {
      notification().error({ title: t('global.errors.unexpected') });
      setShowConfirmModal(false);
      return;
    }

    setConfirmLoading(true);

    onRowDnDEnd(order)
      .then(({ data: { isValid } }: { data: U }) => {
        if (!isValid) {
          notification().error({ title: t('global.errors.unexpected') });

          return;
        }
        refreshTable();
        notification().success({
          title: changeRequestNotificationMessage(t, enableFourEyes),
        });
      })
      .catch(error => {
        notification().error({ title: getApiErrorMessage(error) });
      })
      .finally(() => {
        setShowConfirmModal(false);
        setEditOrder(false);
        setConfirmLoading(false);
      });
  }, [order, onRowDnDEnd, refreshTable, t, enableFourEyes]);

  const handleCancelApply = useCallback(() => setShowConfirmModal(false), []);

  const getRowActions: (record: T) => ReactElement[] = useCallback(
    record => {
      const rowActions = [
        <Tooltip key="view" title={t('enso.buttons.view')} placement="top">
          <Button type="text" aria-label="View Procuration Fee" icon={<EyeOutlined />} onClick={() => onView(record)} />
        </Tooltip>,
        <Guard key="edit" permissions={[`${permissionKey}:update`]} flags={guardFlags}>
          <Tooltip key="edit" title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
            <Button
              type="text"
              aria-label="Edit Procuration Fee"
              icon={<EditOutlined key="edit" />}
              onClick={() => onEdit(record)}
              disabled={!!record.lockedByActionId || readonly}
            />
          </Tooltip>
        </Guard>,
        <Guard key="delete" permissions={[`${permissionKey}:delete`]} flags={guardFlags}>
          <Tooltip key="delete" title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
            <Button
              type="text"
              aria-label="Delete Procuration Fee"
              icon={<DeleteOutlined key="delete" />}
              onClick={() => onDelete(record)}
              disabled={!!record.lockedByActionId || readonly}
            />
          </Tooltip>
        </Guard>,
        changeLogAction(record),
      ];
      return rowActions;
    },
    [onDelete, onEdit, onView, readonly, t, guardFlags, permissionKey, changeLogAction],
  );

  const tableColumns = useMemo(() => columns, [columns]);

  const tableProps: TableProps<T> = useMemo(
    () => ({
      ...tableState,
      id: `${parentEntityType}ProcurationFees`,
      itemsLabel:
        parentEntityType === ActionEntityType.FIRMS
          ? t('firms.labels.procurationFees')
          : t('lender.lender.labels.procurationFees'),
      dataSource: procurationFees ?? [],
      columns: tableColumns,
      rowKey,
      valueLists,
      loading,
      ref: tableRef,
      getRowActions: getRowActions,
      enableRowDnD: editOrder && !!enableRowDnD,
      onRowDnDEnd: ({ newOrder }: { newOrder: T[] }) => setOrder(newOrder),
      pagination: true,
      scroll: { x: 'max-content', y: '100%' },
      resizableColumns: parentEntityType === ActionEntityType.LENDERS,
    }),
    [tableState, procurationFees, tableColumns, valueLists, loading, getRowActions, editOrder, enableRowDnD, parentEntityType, t],
  );

  return (
    <>
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              {editOrder ? (
                <>
                  <Button onClick={() => setEditOrder(false)}>{t('enso.buttons.cancel')}</Button>
                  <Button color="primary" variant="solid" icon={<CheckOutlined />} onClick={onApplyOrder}>
                    {t('enso.buttons.apply')}
                  </Button>
                </>
              ) : (
                <>
                  <Table.ResetFiltersButton />
                  <Table.SelectColumnsButton />
                  <Guard permissions={[`${permissionKey}:update`]} flags={guardFlags}>
                    {enableRowDnD && (
                      <Tooltip key="edit" title={lockActionTitle('edit', orderLockedByActionId)} placement="top">
                        <Button
                          color="primary"
                          icon={<EditOutlined />}
                          onClick={() => {
                            tableRef.current?.resetFilters();
                            setEditOrder(true);
                          }}
                          disabled={isOrderLocked || readonly}
                        >
                          {t('global.buttons.editOrder')}
                        </Button>
                      </Tooltip>
                    )}
                  </Guard>
                  <Guard permissions={[`${permissionKey}:create`]} flags={guardFlags}>
                    {onCreate && <Table.CreateButton onClick={onCreate} disabled={readonly} />}
                  </Guard>
                </>
              )}
            </Space>
          </Flex>
        </Table.ActionsBar>
      </Table>
      <Modal
        open={showConfirmModal}
        title={t('lender.lender.labels.applyOrder')}
        onOk={handleConfirmApply}
        onCancel={handleCancelApply}
        okText={t('enso.buttons.apply')}
        cancelText={t('enso.buttons.cancel')}
        confirmLoading={confirmLoading}
      >
        <Text>{t('lender.lender.labels.confirmApplyFeesOrder')}</Text>
      </Modal>
      {changeLogDrawer}
    </>
  );
};

export const ProcurationFeeAssignmentsList = memo(ProcurationFeeAssignmentsListBase) as <T, U>(
  props: ProcurationFeeAssignmentsListProps<T, U>,
) => ReactElement;
